import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, User, MapPin, Briefcase, Award, Image, Link as LinkIcon, CheckCircle, Upload, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const CreateProfilePage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [portfolioFiles, setPortfolioFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    currentCity: '',
    currentState: '',
    experience: '',
    skills: '',
    previousProjects: '',
    imdbLink: '',
    email: '',
    phone: '',
    departments: []  // Multiple departments selection
  });

  const departments = [
    'Art Department',
    'Camera Department',
    'Sound Department',
    'Makeup & Hair',
    'SFX / VFX',
    'Production',
    'Assistant Directors',
    'Lighting Department',
    'Post Production',
    'Script Department',
    'Costume Department',
    'General Equipment'
  ];

  const handleDepartmentToggle = (dept) => {
    const deptId = dept.toLowerCase().replace(/\s+/g, '-');
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.includes(deptId)
        ? prev.departments.filter(d => d !== deptId)
        : [...prev.departments, deptId]
    }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isUnder10MB = file.size <= 10 * 1024 * 1024;
      
      if (!isImage) {
        toast.error(`${file.name} is not an image file`);
        return false;
      }
      if (!isUnder10MB) {
        toast.error(`${file.name} exceeds 10MB limit`);
        return false;
      }
      return true;
    });
    
    if (validFiles.length > 0) {
      setPortfolioFiles(prev => [...prev, ...validFiles.slice(0, 5 - prev.length)]);
    }
  };

  const removeFile = (index) => {
    setPortfolioFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadPortfolioFiles = async () => {
    if (portfolioFiles.length === 0) return [];
    
    setUploadingFiles(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      
      portfolioFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await fetch(`${backendUrl}/api/uploads/portfolio`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload files');
      }
      
      const data = await response.json();
      return data.files.map(f => ({
        title: f.original_name,
        image_url: f.file_url
      }));
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Some files could not be uploaded');
      return [];
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.departments.length === 0) {
      toast.error('Please select at least one department');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Upload portfolio files first if any
      let portfolioData = [];
      if (portfolioFiles.length > 0) {
        toast.info('Uploading portfolio images...');
        portfolioData = await uploadPortfolioFiles();
      }
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Prepare profile data for API
      const profileData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        designation: formData.designation,
        departments: formData.departments,
        location: {
          city: formData.currentCity,
          state: formData.currentState
        },
        experience: parseInt(formData.experience) || 0,
        skills: formData.skills.split(',').map(s => s.trim()).filter(s => s),
        previous_projects: formData.previousProjects.split('\n').map(p => p.trim()).filter(p => p),
        imdb_link: formData.imdbLink || null,
        portfolio: portfolioData
      };
      
      const response = await fetch(`${backendUrl}/api/users/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(profileData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create profile');
      }
      
      const createdProfile = await response.json();
      
      toast.success('✅ Profile created successfully! You will appear in department listings.');
      
      // Redirect to the first selected department's detail page
      setTimeout(() => {
        navigate(`/departments/${formData.currentState}/${formData.currentCity}/${formData.departments[0]}`);
      }, 2000);
      
    } catch (error) {
      console.error('Profile creation error:', error);
      toast.error(error.message || 'Failed to create profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Form Section */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            <Card className="bg-card border-border shadow-xl">
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-center">
                  Create Your Professional Profile
                </CardTitle>
                <p className="text-center text-muted-foreground mt-2">
                  Join thousands of film professionals on Shoot Base
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <User className="w-5 h-5 text-accent" />
                      Basic Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Full Name *</label>
                        <Input
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="e.g., Rajesh Kumar"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Designation *</label>
                        <Input
                          required
                          value={formData.designation}
                          onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                          placeholder="e.g., Art Director"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Location & Experience */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-accent" />
                      Location & Experience
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Current City *</label>
                        <Input
                          required
                          value={formData.currentCity}
                          onChange={(e) => setFormData({ ...formData, currentCity: e.target.value })}
                          placeholder="e.g., Mumbai"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Current State *</label>
                        <Input
                          required
                          value={formData.currentState}
                          onChange={(e) => setFormData({ ...formData, currentState: e.target.value })}
                          placeholder="e.g., Maharashtra"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Years of Experience *</label>
                        <Input
                          required
                          type="number"
                          value={formData.experience}
                          onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                          placeholder="e.g., 10"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Department Selection */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-accent" />
                      Departments *
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Select all departments you work in. Your profile will appear in each selected department.
                    </p>
                    <div className="grid md:grid-cols-2 gap-3 p-4 bg-muted/30 rounded-lg max-h-64 overflow-y-auto">
                      {departments.map((dept) => {
                        const deptId = dept.toLowerCase().replace(/\s+/g, '-');
                        const isSelected = formData.departments.includes(deptId);
                        return (
                          <div key={dept} className="flex items-center space-x-2">
                            <Checkbox
                              id={deptId}
                              checked={isSelected}
                              onCheckedChange={() => handleDepartmentToggle(dept)}
                            />
                            <label
                              htmlFor={deptId}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {dept}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                    {formData.departments.length > 0 && (
                      <p className="text-sm text-accent flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        {formData.departments.length} department(s) selected
                      </p>
                    )}
                  </div>

                  {/* Skills */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Award className="w-5 h-5 text-accent" />
                      Skills & Expertise
                    </h3>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Skills (comma separated) *</label>
                      <Input
                        required
                        value={formData.skills}
                        onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                        placeholder="e.g., Set Design, 3D Visualization, Period Films"
                      />
                    </div>
                  </div>

                  {/* Previous Projects */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-accent" />
                      Previous Work
                    </h3>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Previous Projects (one per line)</label>
                      <Textarea
                        value={formData.previousProjects}
                        onChange={(e) => setFormData({ ...formData, previousProjects: e.target.value })}
                        placeholder="Film A (2023)&#10;Film B (2022)&#10;Web Series C (2021)"
                        rows={4}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">IMDB Profile Link</label>
                      <Input
                        value={formData.imdbLink}
                        onChange={(e) => setFormData({ ...formData, imdbLink: e.target.value })}
                        placeholder="https://www.imdb.com/name/nm..."
                      />
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Contact Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Email Address *</label>
                        <Input
                          required
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="your.email@example.com"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Phone Number *</label>
                        <Input
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+91 98765 43210"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Portfolio Images Upload */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Image className="w-5 h-5 text-accent" />
                      Portfolio Images (Optional)
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Upload up to 5 images showcasing your work (JPG, PNG, GIF - Max 10MB each)
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                          id="portfolio-upload"
                          disabled={portfolioFiles.length >= 5}
                        />
                        <label
                          htmlFor="portfolio-upload"
                          className={`flex items-center gap-2 px-4 py-2 border border-border rounded-lg cursor-pointer hover:bg-accent/5 transition-colors ${
                            portfolioFiles.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                        >
                          <Upload className="w-4 h-4" />
                          <span className="text-sm">Choose Images</span>
                        </label>
                        <span className="text-sm text-muted-foreground">
                          {portfolioFiles.length}/5 selected
                        </span>
                      </div>
                      
                      {portfolioFiles.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {portfolioFiles.map((file, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`Portfolio ${index + 1}`}
                                className="w-full h-32 object-cover rounded-lg border border-border"
                              />
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <p className="text-xs text-muted-foreground mt-1 truncate">
                                {file.name}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button 
                      type="submit" 
                      disabled={isSubmitting || uploadingFiles}
                      className="w-full bg-accent hover:bg-accent/90 text-white py-6 text-lg"
                    >
                      {uploadingFiles ? 'Uploading images...' : isSubmitting ? 'Creating Profile...' : 'Create Profile & Get Listed'}
                    </Button>
                    <p className="text-xs text-center text-muted-foreground mt-4">
                      By creating a profile, you agree to our Terms of Service and Privacy Policy
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CreateProfilePage;